import { motion, type Variants } from "framer-motion";
import { Logo } from "../Logo";

const container: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    filter: "blur(8px)",
    transition: { duration: 0.45, ease: "easeInOut" },
  },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const Loading = () => {
  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      exit="exit"
      className="relative flex h-screen w-full flex-col items-center justify-center overflow-hidden bg-[#090B09]"
    >
      {/* ── Ambient glow ── */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute h-[520px] w-[520px] rounded-full bg-[#99E39E] blur-[160px]"
        animate={{ opacity: [0.05, 0.12, 0.05], scale: [1, 1.15, 1] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* ── Subtle dot grid ── */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* ── Content ── */}
      <div className="relative z-10 flex flex-col items-center gap-12">
        {/* Logo */}
        <motion.div variants={fadeUp} className="relative">
          {/* Breathing effect */}
          <motion.div
            animate={{ scale: [1, 1.03, 1] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Logo className="h-40 w-auto" />
          </motion.div>

          {/* Shimmer sweep */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <motion.div
              className="h-full w-1/4 bg-gradient-to-r from-transparent via-white/20 to-transparent blur-md"
              initial={{ x: "-300%" }}
              animate={{ x: "500%" }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                repeatDelay: 0.8,
                ease: "easeInOut",
              }}
            />
          </div>
        </motion.div>

        {/* Progress bar + label */}
        <motion.div
          variants={fadeUp}
          className="flex flex-col items-center gap-6"
        >
          <div className="h-[3px] w-60 overflow-hidden rounded-full bg-white/[0.08]">
            <motion.div
              className="h-full w-2/5 rounded-full bg-gradient-to-r from-transparent via-[#99E39E] to-transparent"
              animate={{ x: ["-250%", "350%"] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.35em] text-white/40">
            <span>Loading</span>
            <span className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="inline-block h-1 w-1 rounded-full bg-[#99E39E]"
                  animate={{ opacity: [0.15, 1, 0.15], y: [0, -4, 0] }}
                  transition={{
                    duration: 1.1,
                    repeat: Infinity,
                    delay: i * 0.18,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Loading;